import React, { useEffect, useRef } from "react";
import styles from "./timeout.module.css";

export interface ModalProps {
  isVisible: boolean;
  content: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  theme?: "dark" | "white";
  trapFocus?: boolean;
  autoFocus?: boolean;
}

const TimeoutModal = ({
  isVisible = false,
  content,
  footer,
  size = "md",
  theme = "dark",
  trapFocus = true,
  autoFocus = true,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const focusableElements =
      modalRef.current?.querySelectorAll(focusableSelector);

    const firstElement = focusableElements?.[0] as HTMLElement | undefined;
    const lastElement = focusableElements?.[focusableElements.length - 1] as
      | HTMLElement
      | undefined;

    if (autoFocus && firstElement) {
      firstElement.focus();
    }

    const trapFocusHandler = (event: KeyboardEvent) => {
      if (!trapFocus || event.key !== "Tab") return;
      if (!firstElement || !lastElement) return;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", trapFocusHandler);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", trapFocusHandler);
    };
  }, [isVisible, autoFocus, trapFocus]);

  if (!isVisible) return null;

  return (
    <div className={styles.modal} role="presentation">
      <div
        className={`${styles.modalDialog} ${styles[size]} ${styles[theme]}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-description"
        onClick={(event) => event.stopPropagation()}
        ref={modalRef}
      >
        <div className={styles.modalBody}>
          <div className={styles.modalContent}>{content}</div>
        </div>

        {footer && <div className={styles.modalFooter}>{footer}</div>}
      </div>
    </div>
  );
};

export default TimeoutModal;
